/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2021 Adobe
*  All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import AnalyticsUtil from './AnalyticsUtil';

export default class AppHomeAnalytics {
	ingest: ingestObject;
	analyticsUtils: AnalyticsUtil;

	constructor() {
		this.analyticsUtils = new AnalyticsUtil();
		this.ingest = this.analyticsUtils.ingest;
	}

	/**
	 * [sendToIngest Post to ingest]
	 * @returns {[none]}
	 */
	sendToIngest(): void {
		this.analyticsUtils.checkOptin(this.ingest);
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @returns {[none]}
	 */
	initAnalytics(): void {
		this.ingest.initializeDefaultData();
	}

	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @returns {[none]}
	 */
	logAnalytics(dataObject: dataObject): void {
		dataObject.setEventCategory = 'WEB';
		this.analyticsUtils.logAnalytics(dataObject);
	}


	/*************************************************************************
	* AppHome Component Events
	**************************************************************************/


	/**
	 * [postAppHomeRender Send event for rendering apphomes]
	 * @returns {[none]}
	 */
	postAppHomeRender(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Home',
			setEventSubcategory: 'Main',
			setEventType: 'render',
			setEventSubtype: 'page',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postAppHomeRenderSuccess Send success or eerror on loading apphomes]
	 * @param {boolean} renderSuccess true on a successful render of apphomes
	 * @returns {[none]}
	 */
	postAppHomeRenderSuccess(renderSuccess: boolean, renderDuration: number): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Home',
			setEventSubcategory: 'Main',
			setEventType: renderSuccess ? 'success' : 'error',
			setEventSubtype: 'page',
			setEventValue: `${renderDuration}`,
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/*************************************************************************
	* AppHome Banner / Take Tour Analytic Events
	**************************************************************************/

	/**
	 * [postTourButtonClick Tour button click event]
	 * @param {string} bannerText text of the tour button's banner
	 * @param {string} tourUrl destination url for user
	 * @returns {[none]}
	 */
	postTourButtonClick(bannerText: string, tourUrl: string): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Navigation',
			setEventSubcategory: 'Banner',
			setEventType: 'click',
			setEventSubtype: `take-the-tour / ${bannerText}`,
			setContentAction: tourUrl,
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postBannerDismiss post Banner dismissed event]
	 * @returns {[none]}
	 */
	postBannerDismiss(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Navigation',
			setEventSubcategory: 'Banner',
			setEventType: 'click',
			setEventSubtype: 'close',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/*************************************************************************
	* AppHome Template & Tutorial Analytics Events
	**************************************************************************/

	/**
	 * [postAppHomeCardRender Send event for clicking tutorial/template card on learn tab]
	 * @param {string} cardId Id for the card
	 * @param {string} cardName Name for the card
	 * @param {boolean} cardType type of the card (template, tutorial)
	 * @param {string} tab tab which the card appears in
	 * @param {string} handsOnTutorial true if tutorial is hands-on otherwise false (video tutorial)
	 * @param {boolean} tutorialUrl url of video tutorial
	 * @returns {[none]}
	 */
	postAppHomeCardRender(cardId: string, cardName: string, cardType: string, tab: string, handsOnTutorial: boolean, tutorialUrl: string): void {
		this.initAnalytics();

		const data:any = {
			setEventWorkflow: 'Navigation',
			setEventType: 'render',
			setEventSubtype: cardType,
			setContentID: cardId,
			setContentName: cardName,
			setEventSubcategory: tab,
		};

		if (cardType === 'tutorial') {
			data.setContentType = handsOnTutorial ? 'hands-on-tutorial' : 'video-tutorial';
			data.setContentAction = handsOnTutorial ? '' : tutorialUrl;
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postAppHomeCardClick Send event for clicking tutorial/template card on learn tab]
	 * @param {string} cardId Id for the card
	 * @param {string} cardName Name for the card
	 * @param {boolean} cardType type of the card (template, tutorial)
	 * @param {string} tab tab which the card appears in
	 * @param {string} handsOnTutorial true if tutorial is hands-on otherwise false (video tutorial)
	 * @param {boolean} tutorialUrl url of video tutorial
	 * @returns {[none]}
	 */
	postAppHomeCardClick(cardId: string, cardName: string, cardType: string, tab: string, handsOnTutorial: boolean, tutorialUrl: string): void {
		this.initAnalytics();

		const data:any = {
			setEventWorkflow: 'Navigation',
			setEventType: 'click',
			setEventSubtype: cardType,
			setContentID: cardId,
			setContentName: cardName,
			setEventSubcategory: tab,
		};

		if (cardType === 'tutorial') {
			data.setContentType = handsOnTutorial ? 'hands-on-tutorial' : 'video-tutorial';
			data.setContentAction = handsOnTutorial ? '' : tutorialUrl;
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/*************************************************************************
	* AppHome Operation Card / Click Analytics Events
	**************************************************************************/

	/**
	 * [postNewFileCardClick Send event for select new file card click]
	 * @returns {[none]}
	 */
	postNewFileCardClick(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: 'click',
			setEventSubtype: 'new-file',
			setUIViewType: 'card',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postSelectFileCardClick Send event for select new file label click]
	 * @returns {[none]}
	 */
	postSelectFileCardClick(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: 'click',
			setEventSubtype: 'select-a-file',
			setUIViewType: 'card',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}


	/**
	 * [postNewFileOpenFromComputerClick Send event for new file open from computer]
	 * @returns {[none]}
	 */
	postNewFileOpenFromComputerClick(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: 'click',
			setEventSubtype: 'open-from-your-computer',
			setUIViewType: 'button',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postNewFileOpenFromCloudClick Send event for new file open from cloud]
	 * @returns {[none]}
	 */
	postNewFileOpenFromCloudClick(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: 'click',
			setEventSubtype: 'open-from-the-cloud',
			setUIViewType: 'button',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postNewCanvasCardClick Send click event for new canvas card]
	 * @returns {[none]}
	 */
	postNewCanvasCardClick(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: 'click',
			setEventSubtype: 'new-canvas',
			setUIViewType: 'card',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postNewCanvasButtonClick Send click event for new canvas button]
	 * @returns {[none]}
	 */
	postNewCanvasButtonClick(): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: 'click',
			setEventSubtype: 'new-canvas',
			setUIViewType: 'button',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/*************************************************************************
	* AppHome New File Success / Error Events
	**************************************************************************/

	/**
	 * [postImportFileSuccess Send success or error depending on file import]
	 * @param {boolean} importSuccess true on successful file import
	 * @param {string} fileId id of import file
	 * @param {string} fileExtention extension of import file
	 * @param {string} fileMimetype mimetype of import file
	 * @param {string} fileCategory category of import file
	 * @param {string} fileUrl url of import file
	 * @returns {[none]}
	 */
	postImportFileSuccess(importSuccess: boolean, fileId: string, fileExtention: string, fileMimetype: string, fileCategory: string, fileUrl: string): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: importSuccess ? 'success' : 'error',
			setEventSubtype: 'import',
			setContentID: fileId,
			setContentExtension: fileExtention,
			setContentMimetype: fileMimetype,
			setContentCategory: fileCategory,
			setContentAction: fileUrl,
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postCreateNewCanvasSuccess Send success or error depending on successful canvas creation]
	 * @param {boolean} createSuccess true on successful canvas doc creation, else false
	 * @param {string} fileId id of file
	 * @param {string} fileUrl url of file
	 * @returns {[none]}
	 */
	postCreateNewCanvasSuccess(createSuccess: boolean, fileId: string, fileUrl: string): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: createSuccess ? 'success' : 'error',
			setEventSubtype: 'new-canvas',
			setContentID: fileId,
			setContentMimetype: 'application/vnd.adobe.hz.canvas+dcx',
			setContentAction: fileUrl,
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * [postCreateNewFilePSWebSuccess Send success or error depending on successful new file creation for psweb]
	 * @param {boolean} createSuccess true on successful psweb file creation, else false
	 * @param {string} fileId id of file
	 * @param {string} fileExtention extension of file
	 * @param {string} fileMimetype mimetype of file
	 * @param {string} fileCategory category of file
	 * @param {string} fileUrl url of file
	 * @returns {[none]}
	 */
	postCreateNewFilePSWebSuccess(createSuccess: boolean, fileId: string, fileExtention: string, fileMimetype: string, fileCategory: string, fileUrl: string): void {
		this.initAnalytics();

		const data = {
			setEventWorkflow: 'Operations',
			setEventSubcategory: 'Home',
			setEventType: createSuccess ? 'success' : 'error',
			setEventSubtype: 'new-file',
			setContentID: fileId,
			setContentExtension: fileExtention,
			setContentMimetype: fileMimetype,
			setContentCategory: fileCategory,
			setContentAction: fileUrl,
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

}
